/**
 * Auth Store - Zustand State Management für Logto-basierte Authentifizierung
 */

import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

// =============================================================================
// TYPES
// =============================================================================

export interface Permission {
  id: string
  name: string
  description: string | null
  resource: string
  action: string
}

export interface Role {
  id: string
  name: string
  description: string | null
  isSystem: boolean
  permissions: Permission[]
}

export interface User {
  id: string
  email: string
  firstName: string | null
  lastName: string | null
  isActive: boolean
  isVerified: boolean
  logtoUserId?: string | null
  lastLogin: string | null
  createdAt: string
  updatedAt: string
  roles: Role[]
}

export interface TenantInfo {
  tenantId: string
  tenantName: string
  tenantSlug: string
  orgRole: string
}

// Legacy types kept for backward compatibility during transition
export interface AuthTokens {
  accessToken: string
  refreshToken: string
  tokenType: string
}

export interface LoginResponse extends AuthTokens {
  user: User
}

interface AuthState {
  // State
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  tenantId: string | null
  tenants: TenantInfo[]

  // Actions
  setUser: (user: User) => void
  setAuthenticated: (auth: boolean) => void
  setTenants: (tenants: TenantInfo[]) => void
  setTenantId: (id: string) => void
  logout: () => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  clearError: () => void

  // Permission Helpers
  hasPermission: (permission: string) => boolean
  hasRole: (role: string) => boolean
  isAdmin: () => boolean
}

// =============================================================================
// STORE
// =============================================================================

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial State
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      tenantId: null,
      tenants: [],

      // Actions
      setUser: (user) =>
        set({
          user,
          isAuthenticated: true,
        }),

      setAuthenticated: (auth) =>
        set({
          isAuthenticated: auth,
        }),

      setTenants: (tenants) => {
        const state = get()
        const newState: Partial<AuthState> = { tenants }
        // Auto-select first tenant if none selected
        if (!state.tenantId && tenants.length > 0) {
          newState.tenantId = tenants[0].tenantId
        }
        set(newState)
      },

      setTenantId: (id) =>
        set({
          tenantId: id,
        }),

      logout: () =>
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
          tenantId: null,
          tenants: [],
        }),

      setLoading: (loading) =>
        set({
          isLoading: loading,
        }),

      setError: (error) =>
        set({
          error,
          isLoading: false,
        }),

      clearError: () =>
        set({
          error: null,
        }),

      // Permission Helpers
      hasPermission: (permission) => {
        const { user } = get()
        if (!user || !user.roles) return false

        // Admin hat alle Berechtigungen
        if (user.roles.some((r) => r.name === 'admin')) return true

        // Prüfe alle Rollen auf die Berechtigung
        return user.roles.some((role) =>
          role.permissions?.some((p) => p.name === permission)
        )
      },

      hasRole: (roleName) => {
        const { user } = get()
        if (!user || !user.roles) return false
        return user.roles.some((r) => r.name === roleName)
      },

      isAdmin: () => {
        const { user } = get()
        if (!user || !user.roles) return false
        return user.roles.some((r) => r.name === 'admin')
      },
    }),
    {
      name: 'netzcrm-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        tenantId: state.tenantId,
      }),
    }
  )
)

// =============================================================================
// SELECTORS
// =============================================================================

export const selectUser = (state: AuthState) => state.user
export const selectIsAuthenticated = (state: AuthState) => state.isAuthenticated
export const selectIsLoading = (state: AuthState) => state.isLoading
export const selectError = (state: AuthState) => state.error
